import React, { useEffect, useState, useCallback } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import confetti from "canvas-confetti";
import { Heart, Star, MessageCircle, ArrowRight, BellRing, ChefHat, Smartphone, CreditCard, Wallet, CheckCircle2, Clock, Sparkles, Receipt, Plus } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useCart } from "../context/CartContext";

const STATUS_FLOW = [
  { key: "placed", label: "Placed", icon: CheckCircle2 },
  { key: "preparing", label: "In the pan", icon: ChefHat },
  { key: "ready", label: "Plating", icon: Sparkles },
  { key: "served", label: "Served", icon: Heart },
];
const SERVED_STATUSES = ["served", "bill_requested", "paid"];

export default function OrderSuccess() {
  const { slug, oid } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const { setAppendOrderId, setTableNo } = useCart();
  const [order, setOrder] = useState(null);
  const [restaurant, setRestaurant] = useState(null);
  const [reviews, setReviews] = useState({});
  const [calling, setCalling] = useState(false);
  const [payMethod, setPayMethod] = useState("upi");
  const [paying, setPaying] = useState(false);
  const [showPay, setShowPay] = useState(false);

  const loadOrder = useCallback(async () => {
    try {
      const { data } = await api.get(`/customer/orders/${oid}`);
      setOrder(data);
      if (data.paid) setShowPay(false);
      if (data.waiter_called || data.status === "bill_requested") setShowPay(true);
    } catch { /* noop */ }
  }, [oid]);

  useEffect(() => {
    confetti({ particleCount: 120, spread: 85, origin: { y: 0.3 }, colors: ["#E5A93C", "#FF4B3E", "#FFFFFF"] });
    loadOrder();
    (async () => {
      try { const { data } = await api.get(`/customer/restaurant/${slug}`); setRestaurant(data); } catch { /* noop */ }
    })();
    const int = setInterval(loadOrder, 7000);
    return () => clearInterval(int);
  }, [loadOrder, slug]);

  // Celebrate a paid order the first time the screen sees it.
  useEffect(() => {
    if (!order?.paid) return;
    const fireBurst = () => confetti({
      particleCount: 140,
      spread: 100,
      origin: { y: 0.4 },
      colors: ["#10B981", "#E5A93C", "#FFFFFF", "#F97316"],
    });
    fireBurst();
    const t1 = setTimeout(() => confetti({ particleCount: 60, spread: 140, startVelocity: 35, origin: { y: 0.5, x: 0.2 }, colors: ["#E5A93C", "#FFFFFF"] }), 400);
    const t2 = setTimeout(() => confetti({ particleCount: 60, spread: 140, startVelocity: 35, origin: { y: 0.5, x: 0.8 }, colors: ["#10B981", "#E5A93C"] }), 700);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, [order?.paid]);

  const rate = async (item_id, rating) => {
    setReviews((r) => ({ ...r, [item_id]: rating }));
    try {
      await api.post("/customer/reviews", { order_id: oid, item_id, rating });
      toast.success("Thanks for rating!");
    } catch { toast.error("Could not save rating"); }
  };

  const callWaiter = async () => {
    setCalling(true);
    try {
      await api.post(`/customer/orders/${oid}/call-waiter`);
      toast.success("A server is on the way with your bill");
      setShowPay(true);
      loadOrder();
    } catch { toast.error("Could not call waiter"); }
    setCalling(false);
  };

  const pay = async () => {
    setPaying(true);
    try {
      await api.post(`/customer/orders/${oid}/pay`, { method: payMethod });
      toast.success("Payment received — thank you! Visit us again soon 🌸");
      confetti({ particleCount: 80, spread: 70, origin: { y: 0.4 }, colors: ["#10B981", "#E5A93C", "#FFFFFF"] });
      loadOrder();
    } catch { toast.error("Payment failed"); }
    setPaying(false);
  };

  if (!order) {
    return <div className="min-h-screen flex items-center justify-center text-white/50">Loading your order…</div>;
  }

  const isServed = SERVED_STATUSES.includes(order.status);
  const currentStep = Math.max(0, STATUS_FLOW.findIndex((s) => s.key === (order.status === "bill_requested" || order.status === "paid" ? "served" : order.status)));

  return (
    <div className="min-h-screen relative overflow-hidden pb-24" style={{ background: "var(--bg)", color: "var(--fg)" }}>
      <div className="absolute -top-32 left-1/2 -translate-x-1/2 w-[800px] h-[800px] rounded-full pointer-events-none" style={{ background: "radial-gradient(closest-side, rgba(229,169,60,0.18), transparent)" }} />

      <main className="relative z-10 max-w-2xl mx-auto px-6 pt-12">
        {/* Celebration header */}
        <motion.div initial={{ scale: 0.7, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ type: "spring", damping: 14 }} className="mx-auto w-20 h-20 rounded-full flex items-center justify-center" style={{ background: "rgba(229,169,60,0.15)", border: "1px solid rgba(229,169,60,0.45)" }}>
          <Heart className="w-8 h-8" style={{ color: "var(--accent)" }} />
        </motion.div>

        <motion.h1 initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.2 }} className="font-display text-4xl md:text-5xl mt-6 leading-none text-center">
          {order.paid
            ? `Until next time${user?.name ? `, ${user.name.split(" ")[0]}` : ""} 🌸`
            : (isServed ? "Enjoy every bite." : `${user?.name ? `${user.name.split(" ")[0]}, o` : "O"}rder placed. Now breathe.`)}
        </motion.h1>

        <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.4 }} className="mt-5 text-base leading-relaxed max-w-md mx-auto text-center" style={{ color: "var(--fg-muted)" }}>
          {order.paid
            ? `Your bill is settled — we hope every bite was worth it. ${restaurant?.name ? `The ${restaurant.name.replace(/^The\s+/i, "")} crew` : "We"} can't wait to welcome you back. Leave whenever you like, and come back hungry 💛`
            : isServed
              ? "Savour your meal. When you're ready to leave, tap Call Waiter and we'll bring the bill."
              : "Please bear with us for a short while — our chefs are preparing exotic dishes to elevate your mood. Relax, chat with your company, and let the kitchen do its magic."}
        </motion.p>

        {/* Status tracker */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.55 }} className="mt-10">
          <div className="flex items-center justify-between relative">
            <div className="absolute top-5 left-6 right-6 h-px" style={{ background: "var(--border)" }} />
            <motion.div className="absolute top-5 left-6 h-px" style={{ background: "var(--accent)" }} initial={{ width: 0 }} animate={{ width: `calc(${(currentStep / (STATUS_FLOW.length - 1)) * 100}% - 1.5rem)` }} transition={{ duration: 0.7 }} />
            {STATUS_FLOW.map((s, idx) => {
              const Icon = s.icon;
              const done = idx <= currentStep;
              return (
                <div key={s.key} className="flex flex-col items-center z-10" data-testid={`status-step-${s.key}`}>
                  <div
                    className="w-10 h-10 rounded-full flex items-center justify-center transition-colors"
                    style={
                      done
                        ? { background: "var(--accent)", color: "var(--accent-fg)", border: "1px solid var(--accent)" }
                        : { background: "var(--bg-card)", color: "var(--fg-dim)", border: "1px solid var(--border)" }
                    }
                  >
                    <Icon className="w-4 h-4" />
                  </div>
                  <div className="mt-2 text-[10px] font-mono uppercase tracking-widest" style={{ color: done ? "var(--accent)" : "var(--fg-dim)" }}>{s.label}</div>
                </div>
              );
            })}
          </div>
        </motion.div>

        {/* Order summary */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.7 }} className="mt-8 p-5 rounded-3xl text-left" style={{ border: "1px solid var(--border)", background: "var(--bg-card)" }} data-testid="order-summary">
          <div className="flex items-center justify-between">
            <div className="text-[11px] font-mono uppercase tracking-[0.2em]" style={{ color: "var(--fg-muted)" }}>Order #{order.id.slice(0, 8)} · Table {order.table_no}</div>
            <StatusBadge status={order.status} />
          </div>
          <div className="mt-4 space-y-2">
            {order.items.map((it, idx) => (
              <div key={`${it.item_id}-${idx}`} className="flex items-center justify-between text-sm">
                <span>{it.qty}× {it.name}</span>
                <span className="font-mono" style={{ color: "var(--fg-muted)" }}>₹{(it.price * it.qty).toFixed(0)}</span>
              </div>
            ))}
          </div>
          <div className="mt-4 pt-3 flex justify-between font-display text-lg border-t" style={{ borderColor: "var(--border)" }}>
            <span>{order.paid ? "Paid" : "Total"}</span>
            <span className="gold-text">₹{order.total.toFixed(0)}</span>
          </div>

          {/* Dish reviews (only once served, and only if owner has them enabled) */}
          {isServed && restaurant?.reviews_enabled !== false && (
            <div className="mt-6">
              <div className="flex items-center gap-2" style={{ color: "var(--fg)" }}>
                <MessageCircle className="w-4 h-4" style={{ color: "var(--accent)" }} />
                <span className="text-sm">How was each dish?</span>
              </div>
              <div className="mt-3 space-y-2">
                {order.items.map((it, idx) => (
                  <div key={`${it.item_id}-${idx}`} className="flex items-center justify-between p-2 rounded-xl" style={{ background: "var(--glass-light)" }}>
                    <span className="text-sm">{it.name}</span>
                    <Stars value={reviews[it.item_id] || 0} onChange={(r) => rate(it.item_id, r)} testid={`rate-${it.item_id}`} />
                  </div>
                ))}
              </div>
            </div>
          )}
        </motion.div>

        {/* Call waiter CTA / Payment */}
        <AnimatePresence mode="wait">
          {!order.paid && !showPay && (
            <motion.div key="call" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} transition={{ delay: 0.85 }} className="mt-8 space-y-3">
              <button
                onClick={() => {
                  setAppendOrderId(slug, order.id);
                  if (order.table_no) setTableNo(slug, order.table_no);
                  toast.success("Pick more dishes — they'll join this bill");
                  nav(`/r/${slug}`);
                }}
                data-testid="add-more-btn"
                className="w-full rounded-full px-6 py-3.5 font-semibold inline-flex items-center justify-center gap-2"
                style={{ border: "1px solid #E5A93C", color: "#E5A93C", background: "rgba(229,169,60,0.08)" }}
              >
                <Plus className="w-4 h-4" /> Add more dishes to this order
              </button>
              <div className="text-center">
                <p className="text-xs mb-3 font-mono uppercase tracking-[0.2em]" style={{ color: "var(--fg-muted)" }}>When you're ready to settle</p>
                <button onClick={callWaiter} disabled={calling} data-testid="call-waiter-btn" className="w-full sm:w-auto gold-btn rounded-full px-8 py-4 font-semibold text-base inline-flex items-center gap-2 disabled:opacity-50">
                  <BellRing className="w-5 h-5" /> {calling ? "Ringing…" : "Call Waiter for Bill"}
                </button>
                <p className="text-xs mt-3" style={{ color: "var(--fg-dim)" }}>A server will bring your bill. You'll pay right here.</p>
              </div>
            </motion.div>
          )}

          {!order.paid && showPay && restaurant?.payment_enabled !== false && (
            <motion.div key="pay" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mt-8 p-5 rounded-3xl" style={{ border: "1px solid rgba(229,169,60,0.35)", background: "var(--bg-card)" }} data-testid="payment-section">
              <div className="flex items-center gap-2" style={{ color: "var(--accent)" }}>
                <BellRing className="w-4 h-4" />
                <div className="text-[10px] font-mono uppercase tracking-[0.2em]">Your server is on the way</div>
              </div>
              <h3 className="font-display text-2xl mt-1">Pay your way{user?.name ? `, ${user.name.split(" ")[0]}` : ""}</h3>
              <p className="text-sm mt-1" style={{ color: "var(--fg-muted)" }}>Settle the check at your pace — no queues.</p>
              <div className="mt-4 grid grid-cols-3 gap-2">
                <PayChoice active={payMethod === "upi"} onClick={() => setPayMethod("upi")} icon={<Smartphone className="w-4 h-4" />} label="UPI" testid="pay-upi" />
                <PayChoice active={payMethod === "card"} onClick={() => setPayMethod("card")} icon={<CreditCard className="w-4 h-4" />} label="Card" testid="pay-card" />
                <PayChoice active={payMethod === "wallet"} onClick={() => setPayMethod("wallet")} icon={<Wallet className="w-4 h-4" />} label="Wallet" testid="pay-wallet" />
              </div>
              <button onClick={pay} disabled={paying} data-testid="pay-btn" className="w-full mt-5 gold-btn rounded-full py-3.5 font-semibold disabled:opacity-50">
                {paying ? "Processing…" : `Pay ₹${order.total.toFixed(0)} via ${payMethod.toUpperCase()}`}
              </button>
              <p className="text-[11px] mt-2 text-center" style={{ color: "var(--fg-dim)" }}>Payments are simulated for this demo.</p>
            </motion.div>
          )}

          {!order.paid && showPay && restaurant?.payment_enabled === false && (
            <motion.div key="offline" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mt-8 p-5 rounded-3xl text-center" style={{ border: "1px solid rgba(229,169,60,0.35)", background: "var(--bg-card)" }} data-testid="offline-pay-section">
              <div className="flex items-center justify-center gap-2" style={{ color: "var(--accent)" }}>
                <Receipt className="w-4 h-4" />
                <div className="text-[10px] font-mono uppercase tracking-[0.2em]">Pay at table</div>
              </div>
              <h3 className="font-display text-2xl mt-1">Hand over to your server</h3>
              <p className="text-sm mt-2 leading-relaxed max-w-md mx-auto" style={{ color: "var(--fg-muted)" }}>
                Your waiter will arrive with the bill. Pay in cash, card or any mode you prefer — we'll mark it settled once received.
              </p>
              <div className="mt-4 inline-block px-5 py-3 rounded-xl" style={{ background: "var(--glass-light)", border: "1px solid var(--border)" }}>
                <div className="text-[10px] font-mono uppercase tracking-widest" style={{ color: "var(--fg-muted)" }}>Amount due</div>
                <div className="font-display text-3xl gold-text">₹{order.total.toFixed(0)}</div>
              </div>
            </motion.div>
          )}

          {order.paid && (
            <motion.div
              key="paid"
              initial={{ opacity: 0, y: 24, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              transition={{ type: "spring", damping: 14 }}
              className="mt-8 p-7 rounded-3xl text-center relative overflow-hidden"
              style={{
                border: "1px solid rgba(16,185,129,0.35)",
                background: "linear-gradient(180deg, rgba(16,185,129,0.08) 0%, rgba(229,169,60,0.06) 100%)",
              }}
              data-testid="paid-badge"
            >
              {/* Soft glow */}
              <div className="absolute -top-24 left-1/2 -translate-x-1/2 w-[500px] h-[500px] rounded-full pointer-events-none" style={{ background: "radial-gradient(closest-side, rgba(16,185,129,0.18), transparent)" }} />

              <motion.div
                initial={{ scale: 0.2, rotate: -30 }}
                animate={{ scale: 1, rotate: 0 }}
                transition={{ type: "spring", damping: 10, delay: 0.1 }}
                className="relative z-10 mx-auto w-20 h-20 rounded-full flex items-center justify-center"
                style={{ background: "rgba(16,185,129,0.18)", border: "1px solid rgba(16,185,129,0.5)" }}
              >
                <CheckCircle2 className="w-10 h-10 text-emerald-400" />
              </motion.div>

              <div className="relative z-10 mt-4 text-[11px] font-mono uppercase tracking-[0.3em] text-emerald-300/80">
                Settled · with love
              </div>
              <h3 className="relative z-10 font-display text-3xl md:text-4xl mt-2 leading-tight">
                Thank you{user?.name ? `, ${user.name.split(" ")[0]}` : ""} <span className="inline-block">🌸</span>
              </h3>
              <p className="relative z-10 text-sm md:text-base mt-3 max-w-md mx-auto leading-relaxed" style={{ color: "var(--fg-muted)" }}>
                Your bill is taken care of. We hope every bite made your day a little more delicious
                {restaurant?.name ? ` — the whole team at ${restaurant.name.replace(/^The\s+/i, "")} ` : " — we "}
                absolutely loved having you.
              </p>

              <div className="relative z-10 mt-5 inline-flex items-center gap-2 px-4 py-2 rounded-full" style={{ background: "rgba(229,169,60,0.12)", border: "1px solid rgba(229,169,60,0.4)" }}>
                <Sparkles className="w-3.5 h-3.5" style={{ color: "var(--accent)" }} />
                <span className="text-xs font-mono uppercase tracking-[0.2em]" style={{ color: "var(--accent)" }}>Visit us again soon</span>
              </div>

              <p className="relative z-10 text-xs mt-4 max-w-sm mx-auto" style={{ color: "var(--fg-muted)" }}>
                {restaurant?.reviews_enabled !== false
                  ? "Take a second to rate the dishes above — it helps us pick the perfect suggestion for your next visit."
                  : "Walk out whenever you like — your table is free. We can't wait to feed you again."}
              </p>

              <div className="relative z-10 mt-6 flex items-center justify-center gap-2 flex-wrap">
                <Link
                  to={`/r/${slug}`}
                  data-testid="paid-back-menu"
                  className="gold-btn rounded-full px-5 py-2.5 font-semibold text-sm inline-flex items-center gap-2"
                >
                  Explore the menu <ArrowRight className="w-4 h-4" />
                </Link>
                <button
                  onClick={() => nav(`/r/${slug}/vault`)}
                  data-testid="paid-view-vault"
                  className="rounded-full px-5 py-2.5 text-sm font-semibold inline-flex items-center gap-2"
                  style={{ border: "1px solid var(--border)", color: "var(--fg)" }}
                >
                  My order history
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        <div className="mt-10 flex items-center justify-center gap-3">
          <button onClick={() => nav(`/r/${slug}/vault`)} data-testid="view-vault-btn" className="rounded-full px-5 py-2.5 text-sm inline-flex items-center gap-2" style={{ border: "1px solid var(--border)", color: "var(--fg)" }}>
            Order history <ArrowRight className="w-4 h-4" />
          </button>
          <Link to={`/r/${slug}`} data-testid="back-menu-btn" className="rounded-full px-5 py-2.5 text-sm" style={{ border: "1px solid var(--border)", color: "var(--fg)" }}>Back to menu</Link>
        </div>
      </main>
    </div>
  );
}

function Stars({ value, onChange, testid }) {
  return (
    <div className="flex gap-1" data-testid={testid}>
      {[1, 2, 3, 4, 5].map((n) => (
        <button key={n} onClick={() => onChange(n)} data-testid={`${testid}-${n}`}>
          <Star className="w-5 h-5 transition-colors" style={{ color: n <= value ? "var(--accent)" : "var(--fg-dim)", fill: n <= value ? "var(--accent)" : "transparent" }} />
        </button>
      ))}
    </div>
  );
}

function PayChoice({ active, onClick, icon, label, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className="flex items-center justify-center gap-2 py-3 rounded-xl text-sm transition-colors"
      style={
        active
          ? { background: "var(--accent)", color: "var(--accent-fg)", border: "1px solid var(--accent)", fontWeight: 600 }
          : { background: "transparent", color: "var(--fg)", border: "1px solid var(--border)" }
      }
    >
      {icon} {label}
    </button>
  );
}

function StatusBadge({ status }) {
  const map = {
    placed: { bg: "rgba(245,158,11,0.15)", color: "rgb(252,211,77)" },
    preparing: { bg: "rgba(59,130,246,0.15)", color: "rgb(147,197,253)" },
    ready: { bg: "rgba(16,185,129,0.15)", color: "rgb(110,231,183)" },
    served: { bg: "rgba(229,169,60,0.15)", color: "var(--accent)" },
    bill_requested: { bg: "rgba(249,115,22,0.2)", color: "rgb(253,186,116)" },
    paid: { bg: "rgba(16,185,129,0.2)", color: "rgb(52,211,153)" },
  };
  const s = map[status] || { bg: "var(--glass-light)", color: "var(--fg-muted)" };
  return <span className="text-[10px] px-2 py-1 rounded-full font-mono uppercase tracking-widest" style={{ background: s.bg, color: s.color }}>{status?.replace(/_/g, " ")}</span>;
}
